import { ApiError, createAction } from 'redux-api-middleware';
import { endpointWithUserId } from '../../common/url';
import { State as StoreState } from '../types/State';
import { HTTP_GET } from './ApiAction';

export const LOAD_NOTIFICATIONS = 'barmer-bonus-shared/notifications/LOAD' as const;
export const LOAD_NOTIFICATIONS_SUCCESS = 'barmer-bonus-shared/notifications/SUCCESS' as const;
export const LOAD_NOTIFICATIONS_FAILURE = 'barmer-bonus-shared/notifications/FAILURE' as const;
export const DISMISS_NOTIFICATIONS = 'barmer-bonus-shared/notifications/DISMISS' as const;

type LoadNotifications = { type: typeof LOAD_NOTIFICATIONS; payload?: ApiError; error?: boolean };
type NotificationsUpdate = {
  type: typeof LOAD_NOTIFICATIONS_SUCCESS;
  payload: { notifications: Array<Notification> };
};
type NotificationsFailure = {
  type: typeof LOAD_NOTIFICATIONS_FAILURE;
  payload?: ApiError;
  error?: boolean;
};
type DismissNotification = { type: typeof DISMISS_NOTIFICATIONS };

export type Action =
  | LoadNotifications
  | NotificationsUpdate
  | NotificationsFailure
  | DismissNotification;

export type NotificationType = 'warning' | 'info';

export type Notification = {
  type: NotificationType;
  text: string;
};

export const NOTIFICATIONS_LOADING = 'loading' as const;
export const NOTIFICATIONS_LOADED = 'loaded' as const;
export const NOTIFICATIONS_ERROR = 'error' as const;
export const NOTIFICATIONS_UNKNOWN = 'unknown' as const;
export const NOTIFICATIONS_DISMISSED = 'dismissed' as const;

export type LoadState =
  | typeof NOTIFICATIONS_LOADING
  | typeof NOTIFICATIONS_LOADED
  | typeof NOTIFICATIONS_ERROR
  | typeof NOTIFICATIONS_UNKNOWN
  | typeof NOTIFICATIONS_DISMISSED;

export type State = {
  loading: LoadState;
  notifications: Array<Notification>;
  error?: ApiError;
};

const initialState: State = {
  loading: NOTIFICATIONS_UNKNOWN,
  notifications: [],
  error: undefined,
};

export default function reducer(state: State = initialState, action: Action): State {
  switch (action.type) {
    case LOAD_NOTIFICATIONS:
      return {
        ...state,
        loading: action.error === undefined ? NOTIFICATIONS_LOADING : NOTIFICATIONS_ERROR,
        error: action.error ? action.payload : undefined,
      };
    case LOAD_NOTIFICATIONS_SUCCESS:
      return {
        ...state,
        loading:
          action.payload.notifications.length > 0 ? NOTIFICATIONS_LOADED : NOTIFICATIONS_DISMISSED,
        notifications: action.payload.notifications,
        error: undefined,
      };
    case LOAD_NOTIFICATIONS_FAILURE:
      return {
        ...state,
        loading: NOTIFICATIONS_ERROR,
        error: action.payload,
      };
    case DISMISS_NOTIFICATIONS:
      return {
        ...state,
        loading: NOTIFICATIONS_DISMISSED,
        notifications: [],
        error: undefined,
      };
    default:
      return state;
  }
}

// Whether the notifications have been loaded
export function isLoaded(globalState: StoreState): boolean {
  return (
    globalState.notifications &&
    (NOTIFICATIONS_LOADED === globalState.notifications.loading ||
      NOTIFICATIONS_ERROR === globalState.notifications.loading)
  );
}

export function isLoading(globalState: StoreState): boolean {
  return globalState.notifications && NOTIFICATIONS_LOADING === globalState.notifications.loading;
}

export function hasNotifications(globalState: StoreState): boolean {
  return globalState.notifications && globalState.notifications.notifications.length > 0;
}

/**
 * Load Notifications
 */
export const loadNotifications = (refresh = false, userId = '') =>
  createAction({
    endpoint: endpointWithUserId('/notifications', userId),
    types: [LOAD_NOTIFICATIONS, LOAD_NOTIFICATIONS_SUCCESS, LOAD_NOTIFICATIONS_FAILURE],
    method: HTTP_GET,
    bailout: (state: StoreState) => {
      return (!refresh && isLoaded(state)) || isLoading(state);
    },
  });

/**
 * Dismiss the loaded notifications
 */
export const dismissNotifications = (): Action => {
  return { type: DISMISS_NOTIFICATIONS };
};
